/**
 * "Se historikk" — owner history for one car, from inside Leads (#585).
 *
 * The lookup is the Oppslag module's, reused as-is: this component holds no
 * SQL, no walk-back logic and no second copy of the API contract. What it adds
 * is the two things Leads needs and Oppslag does not have — the tenure per
 * owner (AC4), and a cache so reopening the panel is free (AC5).
 *
 * It renders a modal rather than an expanded table row, because it is mounted
 * from two grids with different column counts and a self-contained overlay
 * cannot get that wrong.
 */
import { useState } from 'react'
import { AlertTriangle, Building2, History, Loader2, User, X } from 'lucide-react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import ExportButtons from '../../../components/ExportButtons'
import { useKanEksportere } from '../eksportTilgang'
import { eierhistorikk, type Eier, type EierhistorikkResponse } from '../../Oppslag/oppslagApi'
import { formatNorwegianDate } from '../../../utils/dateUtils'
import { lesHistorikk, skrivHistorikk } from './cache'
import { eiertidForKjede } from './eiertid'

/** Matches the Oppslag tab. maks_steg bounds SVV lookups, so this is also the
 *  worst-case cost in quota units of one click. */
const MAKS_STEG = 10

type StoppNokkel =
  | 'eierhistorikk.stopp.ingenFlereEiere' | 'eierhistorikk.stopp.maksSteg'
  | 'eierhistorikk.stopp.tidligst' | 'eierhistorikk.stopp.feilmelding' | 'eierhistorikk.stopp.kvote'

const STOPP_TEKST: Record<string, StoppNokkel> = {
  ingen_flere_eiere: 'eierhistorikk.stopp.ingenFlereEiere',
  maks_steg: 'eierhistorikk.stopp.maksSteg',
  tidligst: 'eierhistorikk.stopp.tidligst',
  feilmelding: 'eierhistorikk.stopp.feilmelding',
  kvote: 'eierhistorikk.stopp.kvote',
}

/** Export columns. `eiertid` is the reason this list is not simply the Oppslag
 *  tab's: a dealer exporting the chain wants the tenure, which is the whole
 *  point of the panel. Values are pre-formatted, so no `format` callbacks. */
const EKSPORT_KOLONNER = [
  { key: 'steg', labelKey: 'eierhistorikk.col.steg' },
  { key: 'eier', labelKey: 'eierhistorikk.col.eier' },
  { key: 'eiertype', labelKey: 'eierhistorikk.col.eiertype' },
  { key: 'organisasjonsnummer', labelKey: 'common.orgNr' },
  { key: 'adresse', labelKey: 'eierhistorikk.col.adresse' },
  { key: 'registrert_pa_eier', labelKey: 'eierhistorikk.col.registrertPaEier' },
  { key: 'eiertid', labelKey: 'eierhistorikk.col.eiertid' },
  { key: 'medeier', labelKey: 'eierhistorikk.col.medeier' },
  { key: 'leasingtaker', labelKey: 'eierhistorikk.col.leasingtaker' },
] as const

const EIERTYPE_TEKST: Record<string, 'eierhistorikk.eiertype.organisasjon' | 'eierhistorikk.eiertype.person'> = {
  organisasjon: 'eierhistorikk.eiertype.organisasjon',
  person: 'eierhistorikk.eiertype.person',
}

function adresseAv(part: Eier | null | undefined): string {
  return [part?.adresselinje1, part?.postnummer, part?.poststed]
    .filter(Boolean)
    .join(', ')
}

/** The error as a message key, translated at render so it follows a switch. */
function feilTekst(error: unknown) {
  const status = (error as AxiosError)?.response?.status
  if (status === 429) {
    return 'eierhistorikk.error.quota' as const
  }
  if (status === 503) {
    // Not the same thing as 429, and a quota raise cannot fix it.
    return 'eierhistorikk.error.unavailable' as const
  }
  if (status === 403) {
    return 'eierhistorikk.error.forbidden' as const
  }
  return 'eierhistorikk.error.failed' as const
}

/** The name cell. Org.nr and address have their own columns now, so this is
 *  just the name plus the icon that distinguishes a company from a person —
 *  which matters commercially: a private seller and a dealer are different
 *  conversations. */
function EierNavn({ eier }: { eier: Eier | null }) {
  const { t } = useTranslation('leads')
  if (!eier) {
    return <span className="text-gray-500 dark:text-gray-400">{t('eierhistorikk.noOwner')}</span>
  }
  const Ikon = eier.eier_type === 'organisasjon' ? Building2 : User
  return (
    <span className="inline-flex items-center gap-2">
      <Ikon className="w-4 h-4 text-gray-400 shrink-0" />
      <span className="text-gray-900 dark:text-white">{eier.navn ?? '—'}</span>
    </span>
  )
}

interface Props {
  kjennemerke: string | null
  /** From SVVState. With `registrertPaEier` it lets the walk skip its first
   *  lookup; without it the backend falls back to the plate. */
  kuid?: string | null
  registrertPaEier?: string | null
  /** Current owner's name, known from the Leads row. Shown as the newest link
   *  when the walk started from the kuid and never asked SVV who owns it now. */
  navaerendeEier?: string | null
  merke?: string | null
  modell?: string | null
  registrertForsteGang?: string | null
}

export default function EierhistorikkKnapp({
  kjennemerke,
  kuid,
  registrertPaEier,
  navaerendeEier,
  merke,
  modell,
  registrertForsteGang,
}: Props) {
  const { t } = useTranslation('leads')
  const kanEksportere = useKanEksportere()
  const [apen, setApen] = useState(false)
  const [laster, setLaster] = useState(false)
  const [feil, setFeil] = useState<ReturnType<typeof feilTekst> | null>(null)
  const [svar, setSvar] = useState<EierhistorikkResponse | null>(null)

  // No plate, nothing to look up (AC9).
  if (!kjennemerke) return null

  async function apne() {
    setApen(true)
    setFeil(null)

    const fraCache = lesHistorikk(kjennemerke!)
    if (fraCache) {
      setSvar(fraCache)
      return
    }

    setLaster(true)
    try {
      const start =
        kuid && registrertPaEier ? { kuid, fraDato: registrertPaEier } : undefined
      const resultat = await eierhistorikk(kjennemerke!, MAKS_STEG, start)
      skrivHistorikk(kjennemerke!, resultat)
      setSvar(resultat)
    } catch (error) {
      // Not cached: a 429 today must not become a permanent answer.
      setFeil(feilTekst(error))
    } finally {
      setLaster(false)
    }
  }

  const avkortet = svar?.stoppet_fordi === 'maks_steg'

  /** The chain as shown, newest first.
   *
   *  When the walk started from the kuid, SVV was never asked who owns the car
   *  now — so the current owner is prepended from the Leads row. That is not
   *  cosmetic: without it the previous owner's tenure would be measured to
   *  today instead of to the current owner's start date. */
  const viste = (() => {
    const ledd = svar?.historikk ?? []
    const harNa = ledd.some((l) => l.steg === 0)
    if (harNa || !registrertPaEier) return ledd.map((l) => ({ ...l, lokal: false }))
    return [
      {
        steg: 0,
        eier: {
          eier_type: 'organisasjon' as const,
          navn: navaerendeEier ?? null,
          organisasjonsnummer: null,
          adresselinje1: null,
          adresselinje2: null,
          postnummer: null,
          poststed: null,
          kommunenavn: null,
          land: null,
          registrert_pa_eier: registrertPaEier,
          vedtakstidspunkt: null,
        } as Eier,
        medeier: null,
        leasingtaker: null,
        registrert_pa_eier: registrertPaEier,
        dtg: null,
        lokal: true,
      },
      ...ledd.map((l) => ({ ...l, lokal: false })),
    ]
  })()

  const tider = eiertidForKjede(viste, avkortet)

  /** One row per owner, pre-formatted. The same values the table renders go to
   *  CSV/Excel/PDF, so an export can never disagree with what was on screen. */
  const eksportRader = viste.map((ledd, i) => ({
    steg: ledd.steg === 0 ? t('eierhistorikk.now') : String(ledd.steg),
    eier: ledd.eier?.navn ?? '',
    eiertype: ledd.eier ? (EIERTYPE_TEKST[ledd.eier.eier_type] ? t(EIERTYPE_TEKST[ledd.eier.eier_type]) : ledd.eier.eier_type) : '',
    organisasjonsnummer: ledd.eier?.organisasjonsnummer ?? '',
    adresse: adresseAv(ledd.eier),
    registrert_pa_eier: ledd.registrert_pa_eier
      ? formatNorwegianDate(ledd.registrert_pa_eier)
      : '',
    eiertid: tider[i],
    medeier: ledd.medeier?.navn ?? '',
    leasingtaker: ledd.leasingtaker?.navn ?? '',
  }))

  return (
    <>
      <button
        type="button"
        onClick={apne}
        data-testid={`eierhistorikk-knapp-${kjennemerke}`}
        title={t('eierhistorikk.buttonTitle', { n: MAKS_STEG })}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium
                   text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30
                   hover:bg-primary-100 dark:hover:bg-primary-900/50 whitespace-nowrap"
      >
        <History className="w-3 h-3" />
        {t('eierhistorikk.button')}
      </button>

      {apen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16 bg-black/40"
          onClick={() => setApen(false)}
        >
          <div
            className="card p-5 w-full max-w-5xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="eierhistorikk-panel"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  {t('eierhistorikk.title')}
                </h3>
                {/* Which car this is, so the answer is never ambiguous (AC7). */}
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  <span className="font-mono text-primary-600 dark:text-primary-400">
                    {kjennemerke}
                  </span>
                  {(merke || modell) && ` · ${[merke, modell].filter(Boolean).join(' ')}`}
                  {registrertForsteGang &&
                    ` · ${t('eierhistorikk.firstReg', { date: formatNorwegianDate(registrertForsteGang) })}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setApen(false)}
                aria-label={t('common.close')}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {laster && (
              <div
                className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-6"
                data-testid="eierhistorikk-laster"
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('eierhistorikk.loading')}
              </div>
            )}

            {feil && (
              <div
                className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300
                           bg-red-50 dark:bg-red-900/20 rounded p-3"
                data-testid="eierhistorikk-feil"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                {t(feil)}
              </div>
            )}

            {!laster && !feil && svar && (
              <>
                {/* Export row above the grid, same placement as the Oppslag
                    tab and every other table in the app. */}
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {viste.length === 1
                      ? t('eierhistorikk.ownersOne', { n: viste.length })
                      : t('eierhistorikk.ownersMany', { n: viste.length })}
                  </span>
                  {kanEksportere && (
                    <ExportButtons
                      data={eksportRader}
                      columns={EKSPORT_KOLONNER.map(({ key, labelKey }) => ({ key, label: t(labelKey) }))}
                      metadata={{
                        title: t('eierhistorikk.exportTitle', { plate: kjennemerke }),
                        // The completeness reason travels with the export. A PDF
                        // cut short at maks_steg looks complete otherwise, and a
                        // dealer would read it as the car's whole history.
                        filters: [
                          [merke, modell].filter(Boolean).join(' '),
                          STOPP_TEKST[svar.stoppet_fordi] ? t(STOPP_TEKST[svar.stoppet_fordi]) : svar.stoppet_fordi,
                        ].filter(Boolean),
                      }}
                    />
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="data-table data-table-compact">
                    <thead>
                      <tr>
                        <th className="text-left whitespace-nowrap">{t('eierhistorikk.col.steg')}</th>
                        <th className="text-left whitespace-nowrap">{t('eierhistorikk.col.eier')}</th>
                        <th className="text-left whitespace-nowrap">{t('eierhistorikk.col.eiertype')}</th>
                        <th className="text-left whitespace-nowrap">{t('common.orgNr')}</th>
                        <th className="text-left whitespace-nowrap">{t('eierhistorikk.col.adresse')}</th>
                        <th className="text-left whitespace-nowrap">{t('eierhistorikk.col.registrertPaEier')}</th>
                        <th className="text-left whitespace-nowrap">{t('eierhistorikk.col.eiertid')}</th>
                        <th className="text-left whitespace-nowrap">{t('eierhistorikk.col.medeier')}</th>
                        <th className="text-left whitespace-nowrap">{t('eierhistorikk.col.leasingtaker')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viste.map((ledd, i) => (
                        <tr
                          key={`${ledd.steg}-${ledd.registrert_pa_eier ?? i}`}
                          className={
                            ledd.steg === 0
                              ? 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-primary-500'
                              : i % 2 === 0
                                ? 'bg-gray-50 dark:bg-gray-800/30'
                                : ''
                          }
                        >
                          <td className="text-xs text-gray-500 whitespace-nowrap">
                            {ledd.steg === 0 ? t('eierhistorikk.now') : ledd.steg}
                          </td>
                          <td className="font-medium whitespace-nowrap">
                            <EierNavn eier={ledd.eier} />
                          </td>
                          <td className="text-xs text-gray-500 whitespace-nowrap">
                            {ledd.eier
                              ? (EIERTYPE_TEKST[ledd.eier.eier_type] ? t(EIERTYPE_TEKST[ledd.eier.eier_type]) : ledd.eier.eier_type)
                              : '—'}
                          </td>
                          <td className="font-mono text-xs text-gray-500 whitespace-nowrap">
                            {ledd.eier?.organisasjonsnummer ?? '—'}
                          </td>
                          <td className="text-xs text-gray-500">
                            {adresseAv(ledd.eier) || '—'}
                          </td>
                          <td className="text-xs whitespace-nowrap">
                            {ledd.registrert_pa_eier
                              ? formatNorwegianDate(ledd.registrert_pa_eier)
                              : '—'}
                          </td>
                          <td
                            className="text-xs font-medium whitespace-nowrap"
                            data-testid={`eiertid-${ledd.steg}`}
                          >
                            {tider[i]}
                          </td>
                          {/* Co-owner and lessee AT THAT STEP, not today's. */}
                          <td className="text-xs text-amber-700 dark:text-amber-300 whitespace-nowrap">
                            {ledd.medeier?.navn ?? '—'}
                          </td>
                          <td className="text-xs text-indigo-700 dark:text-indigo-300 whitespace-nowrap">
                            {ledd.leasingtaker?.navn ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div
                  data-testid="eierhistorikk-stopp"
                  className={`mt-4 text-xs rounded p-2 ${
                    svar.stoppet_fordi === 'ingen_flere_eiere'
                      ? 'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50'
                      : 'text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20'
                  }`}
                >
                  {STOPP_TEKST[svar.stoppet_fordi] ? t(STOPP_TEKST[svar.stoppet_fordi]) : svar.stoppet_fordi}
                </div>

                <div className="mt-2 text-xs text-gray-400">
                  {t('eierhistorikk.notStored')}
                  {svar.kvote?.maskinporten && (
                    <> {t('eierhistorikk.quotaLeft', { n: svar.kvote.maskinporten.gjenstaende })}</>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}
